import { useLazyQuery } from "@apollo/client";
import { IonSkeletonText } from "@ionic/react";
import { addDays, isSameDay, nextSaturday, previousSunday, subDays } from "date-fns";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormattedDateParts, FormattedMessage, useIntl } from "react-intl";

import ExpandableSection from "@components/animations/ExpandableSection";
import ActionIconButton from "@components/buttons/ActionIconButton";
import PrimaryButton from "@components/buttons/PrimaryButton";
import ExpertTypeSelect from "@components/expertSection/schedule/ExpertTypeSelect";
import styles from "@components/expertSection/schedule/ScheduleSession.module.css";
import RadioButtonGroup from "@components/fieldsNative/RadioButtonGroup";
import TimeSelector from "@components/TimeSelector";
import BodyCopy from "@components/typography/BodyCopy";
import ButtonLink from "@components/typography/ButtonLink";
import Heading1 from "@components/typography/Heading1";
import useNow from "@hooks/useNow";
import { ExpertSelectTypeEnum } from "@typing/Enums";
import {
  ConsultAvailabilityFragment as ConsultAvailabilityType,
  ConsultDurationEnum,
  GetConsultAvailabilityDaysDocument,
  GetConsultAvailabilityDaysQuery,
  GetConsultAvailabilityDaysQueryVariables,
  GetJourneyWithExpertAndConsultsQuery
} from "@typing/Generated";
import { clickCurrentTarget } from "@utils/keyboardUtils";
import { primaryExpertRole } from "@utils/modelUtils";
import { dateToIso8601 } from "@utils/timeUtils";

type Props = {
  availability?: ConsultAvailabilityType;
  defaultDate?: Date | null;
  duration: ConsultDurationEnum;
  journey: GetJourneyWithExpertAndConsultsQuery["journey"];
  onAvailabilitySelected: (availability: ConsultAvailabilityType | undefined) => void;
};

type AvailabilityGroupProps = {
  availabilities: ConsultAvailabilityType[];
  availabilitySelected: ConsultAvailabilityType | null;
  expertSelectType: ExpertSelectTypeEnum;
  handleAvailabilitySelect: (availability: ConsultAvailabilityType) => void;
  journey: Props["journey"];
  titleKey: string;
};

const AvailabilityGroup = ({
  availabilities,
  availabilitySelected,
  expertSelectType,
  handleAvailabilitySelect,
  journey,
  titleKey
}: AvailabilityGroupProps) => {
  const intl = useIntl();
  if (availabilities.length === 0) return null;

  return (
    <div>
      <div className={styles.timeOfDay}>
        <BodyCopy color="primary">
          <FormattedMessage id={titleKey} />
        </BodyCopy>
      </div>
      <div className={styles.availabilities}>
        {availabilities.map(availability => (
          <div key={availability.startTime}>
            <TimeSelector
              clientRoles={journey.clientRoles}
              expert={expertSelectType === ExpertSelectTypeEnum.ANY ? availability.expert : undefined}
              large
              onSelect={() => handleAvailabilitySelect(availability)}
              selected={availability.startTime.getTime() == availabilitySelected?.startTime.getTime()}
              text={intl.formatTime(availability.startTime, { hour: "numeric", minute: "numeric" })}
              value={availability.startTime}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const AvailabilitySelect = ({ availability, defaultDate, duration, journey, onAvailabilitySelected }: Props) => {
  const intl = useIntl();
  const buttonsRef = useRef<HTMLDivElement>(null);
  const availabilitiesRef = useRef<HTMLDivElement>(null);

  const [getAvailabilityDays, getAvailabilityDaysQuery] = useLazyQuery<
    GetConsultAvailabilityDaysQuery,
    GetConsultAvailabilityDaysQueryVariables
  >(GetConsultAvailabilityDaysDocument, {
    notifyOnNetworkStatusChange: true
  });

  const now = useNow();

  const defaultStart = useMemo(() => {
    if (availability) {
      return availability.startTime;
    }
    if (defaultDate) {
      const defaultTime = new Date(dateToIso8601(new Date(defaultDate), intl)).getTime();
      const nowTime = now.getTime();
      if (defaultTime > nowTime) {
        return new Date(dateToIso8601(new Date(defaultDate), intl));
      }
      return now;
    }
    return now;
  }, [availability, defaultDate, now, intl]);

  const [hasSkippedWeek, setHasSkippedWeek] = useState(false);
  const [expertType, setExpertType] = useState(ExpertSelectTypeEnum.PRIMARY);
  const [currentDay, setCurrentDay] = useState<Date>(defaultStart);
  const [selectedDay, setSelectedDay] = useState<Date | null>(availability?.startTime ?? null);
  const [availabilitySelected, setAvailabilitySelected] = useState<ConsultAvailabilityType | null>(
    availability ?? null
  );

  const primaryExpert = useMemo(() => primaryExpertRole(journey.expertRoles)?.expert, [journey.expertRoles]);
  const beginningOfWeek = useMemo(() => previousSunday(currentDay), [currentDay]);
  const endOfWeek = useMemo(() => nextSaturday(currentDay), [currentDay]);

  useEffect(() => {
    if (primaryExpert) {
      getAvailabilityDays({
        variables: {
          duration,
          journeyId: journey.id,
          nonPrimaryExperts: expertType === ExpertSelectTypeEnum.ANY,
          startTime: beginningOfWeek
        }
      });
    }
  }, [duration, journey, getAvailabilityDays, primaryExpert, beginningOfWeek, expertType]);

  type MonthOption = {
    date: Date;
    label: string;
    value: number;
  };

  const monthOptions: MonthOption[] = [
    now,
    new Date(now.getFullYear(), now.getMonth() + 1, 2),
    new Date(now.getFullYear(), now.getMonth() + 2, 2)
  ].map(date => ({
    date,
    label: intl.formatDate(date, { month: "long" }),
    value: date.getMonth()
  }));

  const handleMonthChange = (option: MonthOption) => {
    if (option.value !== currentDay.getMonth() && option.date) {
      setCurrentDay(option.date);
      setSelectedDay(null);
    }
  };

  const handleSelectedDayChange = (date: Date) => {
    setSelectedDay(date);
    setAvailabilitySelected(null);
    onAvailabilitySelected(undefined);
    availabilitiesRef?.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleAvailabilitySelected = (availability: ConsultAvailabilityType) => {
    setAvailabilitySelected(availability);
    setTimeout(() => buttonsRef.current?.scrollIntoView({ behavior: "smooth" }), 400);
  };

  const outOfScopeMonth = new Date(now.getFullYear(), now.getMonth() + 3, 2).getMonth();

  const nextWeekAllowed = useMemo(() => {
    const newDate = addDays(beginningOfWeek, 7);

    return newDate.getMonth() !== outOfScopeMonth;
  }, [beginningOfWeek, outOfScopeMonth]);

  const previousWeekAllowed = useMemo(() => subDays(endOfWeek, 7) > now, [endOfWeek, now]);

  const handlePreviousWeek = useCallback(() => {
    setSelectedDay(null);
    setAvailabilitySelected(null);
    onAvailabilitySelected(undefined);
    const newDate = subDays(currentDay, 7);

    setCurrentDay(newDate);
  }, [currentDay, onAvailabilitySelected]);

  const handleNextWeek = useCallback(() => {
    setSelectedDay(null);
    setAvailabilitySelected(null);
    onAvailabilitySelected(undefined);

    const newDate = addDays(currentDay, 7);

    setCurrentDay(newDate);
  }, [currentDay, onAvailabilitySelected]);

  const selectedAvailabilityDay = useMemo(
    () =>
      selectedDay &&
      getAvailabilityDaysQuery.data?.consultAvailabilityDays.find(day => isSameDay(selectedDay, day.date)),
    [getAvailabilityDaysQuery.data?.consultAvailabilityDays, selectedDay]
  );

  const availabilities = useMemo(() => {
    const morningDates: ConsultAvailabilityType[] = [];
    const eveningDates: ConsultAvailabilityType[] = [];

    for (const availability of selectedAvailabilityDay?.availabilities ?? []) {
      const hours = availability.startTime.getHours();

      if (hours <= 12) {
        morningDates.push(availability);
      } else if (hours > 12 && hours <= 24) {
        eveningDates.push(availability);
      }
    }

    return { eveningDates, morningDates };
  }, [selectedAvailabilityDay?.availabilities]);

  useEffect(() => {
    if (
      hasSkippedWeek === false &&
      currentDay.getTime() == defaultStart.getTime() &&
      getAvailabilityDaysQuery.data?.consultAvailabilityDays &&
      !getAvailabilityDaysQuery.data?.consultAvailabilityDays.slice(0, 7).some(day => day.availabilities.length > 0) &&
      nextWeekAllowed
    ) {
      handleNextWeek();
      setHasSkippedWeek(true);
    }
  }, [
    currentDay,
    defaultStart,
    getAvailabilityDaysQuery.data?.consultAvailabilityDays,
    handleNextWeek,
    hasSkippedWeek,
    nextWeekAllowed
  ]);

  return (
    <div className={styles.select}>
      <Heading1>
        <FormattedMessage id="pages.consults.selectTime.selectTime" />
      </Heading1>
      <ExpertTypeSelect expertType={expertType} journey={journey} setExpertType={setExpertType} />
      <div className={styles.months}>
        <RadioButtonGroup<MonthOption>
          currentSelection={currentDay.getMonth()}
          onChange={handleMonthChange}
          options={monthOptions}
        />
      </div>
      <div className={styles.daysContainer} ref={availabilitiesRef}>
        <ActionIconButton
          ariaLabelKey="accessibility.ariaLabels.previous"
          disabled={!previousWeekAllowed}
          icon="circleArrowLeft"
          onClick={handlePreviousWeek}
          size="medium"
        />

        <div className={styles.days}>
          {[...Array(7).keys()].map(index => {
            const isEmpty =
              getAvailabilityDaysQuery.data?.consultAvailabilityDays.find(day =>
                isSameDay(addDays(beginningOfWeek, index), day.date)
              )?.availabilities.length === 0 || !getAvailabilityDaysQuery.data;

            const date = addDays(beginningOfWeek, index);
            const selected = selectedDay ? isSameDay(date, selectedDay) : false;

            if (date.getMonth() === outOfScopeMonth) return null;

            return (
              <div
                className={`${styles.day} ${selected ? styles.selected : ""} ${isEmpty ? styles.empty : ""}`}
                data-test={isEmpty ? "day-disabled" : "day"}
                key={index.toString()}
                onClick={() => (isEmpty ? {} : handleSelectedDayChange(date))}
                onKeyUp={clickCurrentTarget}
                tabIndex={0}
              >
                <BodyCopy color={selected ? "white" : isEmpty ? "gray-300" : "primary"}>
                  <FormattedDateParts value={date} weekday="short">
                    {parts => <>{parts[0].value.toUpperCase()}</>}
                  </FormattedDateParts>
                </BodyCopy>
                <div>
                  {!getAvailabilityDaysQuery.data ? (
                    <IonSkeletonText animated className={styles.loadingDate} />
                  ) : (
                    <Heading1 color={selected ? "white" : isEmpty ? "gray-300" : "primary"}>{date.getDate()}</Heading1>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <ActionIconButton
          ariaLabelKey="accessibility.ariaLabels.next"
          disabled={!nextWeekAllowed}
          icon="circleArrowRight"
          onClick={handleNextWeek}
          size="medium"
        />
      </div>
      <ExpandableSection
        className={styles.fullWidth}
        isOpen={!!selectedAvailabilityDay}
        key="availabilities"
        onAnimationComplete={() => availabilitiesRef?.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
      >
        <div>
          <AvailabilityGroup
            availabilities={availabilities.morningDates}
            availabilitySelected={availabilitySelected}
            expertSelectType={expertType}
            handleAvailabilitySelect={handleAvailabilitySelected}
            journey={journey}
            titleKey="pages.consults.selectTime.morning"
          />
          <AvailabilityGroup
            availabilities={availabilities.eveningDates}
            availabilitySelected={availabilitySelected}
            expertSelectType={expertType}
            handleAvailabilitySelect={handleAvailabilitySelected}
            journey={journey}
            titleKey="pages.consults.selectTime.afternoon"
          />
        </div>
        <div className={styles.cantFind}>
          <BodyCopy color="gray-500">
            <FormattedMessage
              id="pages.consults.selectTime.cantFind"
              values={{
                link: (
                  <ButtonLink onClick={() => setExpertType(ExpertSelectTypeEnum.ANY)}>
                    <FormattedMessage id="pages.consults.selectTime.searchAnyAvailable" />
                  </ButtonLink>
                )
              }}
            />
          </BodyCopy>
        </div>
      </ExpandableSection>
      <div className={styles.buttonContainer} ref={buttonsRef}>
        <ExpandableSection
          isOpen={!!availabilitySelected}
          key="complete-availability-select"
          transition={{ duration: 0.6 }}
        >
          <PrimaryButton
            disabled={!availabilitySelected}
            icon="chevronDown"
            onClick={() => {
              if (availabilitySelected) {
                onAvailabilitySelected(availabilitySelected);
              }
            }}
            size="large"
            textKey="pages.consults.selectTime.contactMethod"
          />
        </ExpandableSection>
      </div>
    </div>
  );
};

export default AvailabilitySelect;
